import { LinkIcon, Trash2Icon } from "lucide-react"
import { type ReactNode, useState } from "react"
import { toast } from "sonner"
import { CopyableField } from "~/components/CopyableField"
import { Button } from "~/components/ui/button"
import { Card, CardTitle } from "~/components/ui/card"
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogTrigger
} from "~/components/ui/dialog"
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue
} from "~/components/ui/select"
import { getAppUrl } from "~/lib/client-url"
import { confirmAction } from "~/lib/native-dialog"
import { shareUrl } from "~/lib/native-share"
import { api } from "~/trpc/react"

export function KBShareDialog({
	teamId,
	documentId,
	children
}: {
	teamId: string
	documentId: string
	children: ReactNode
}) {
	const [open, setOpen] = useState(false)
	const [duration, setDuration] = useState<
		"24h" | "72h" | "1w" | "1mo" | "forever"
	>("1w")
	const utils = api.useUtils()
	const shares = api.knowledgeBase.shares.useQuery(
		{ teamId, documentId },
		{ enabled: open }
	)
	const createShare = api.knowledgeBase.createShare.useMutation({
		onSuccess: async () => {
			await utils.knowledgeBase.shares.invalidate({ teamId, documentId })
			toast("Share link created")
		}
	})
	const updateShare = api.knowledgeBase.updateShare.useMutation({
		onSuccess: async () => {
			await utils.knowledgeBase.shares.invalidate({ teamId, documentId })
			toast("Share link updated")
		}
	})
	const deleteShare = api.knowledgeBase.deleteShare.useMutation({
		onSuccess: async () => {
			await utils.knowledgeBase.shares.invalidate({ teamId, documentId })
			toast("Share link removed")
		}
	})
	const activeShare = shares.data?.[0]
	const url = activeShare ? getAppUrl(`/share/${activeShare.id}`) : ""
	return (
		<Dialog open={open} onOpenChange={setOpen}>
			<DialogTrigger asChild>{children}</DialogTrigger>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>Share document</DialogTitle>
					<DialogDescription>
						Anyone with the link can view this document until the link expires.
					</DialogDescription>
				</DialogHeader>
				<Card variant="secondary" density="tight">
					<CardTitle className="flex items-center gap-2 text-sm">
						<LinkIcon className="size-4" />
						Public link
					</CardTitle>
					{activeShare && (
						<CopyableField
							value={url}
							label="Public document link"
							onCopy={() =>
								shareUrl({
									title: "Orbit knowledge base document",
									text: "View this Orbit knowledge base document.",
									url,
									dialogTitle: "Share document"
								})
							}
						/>
					)}
					<div className="flex flex-wrap items-center gap-2">
						<Select
							value={duration}
							onValueChange={(value) => setDuration(value as typeof duration)}
						>
							<SelectTrigger className="w-36" aria-label="Link expiry">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{(
									[
										["24h", "24 hours"],
										["72h", "72 hours"],
										["1w", "1 week"],
										["1mo", "1 month"],
										["forever", "Forever"]
									] as const
								).map(([value, label]) => (
									<SelectItem key={value} value={value}>
										{label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
						{activeShare ? (
							<>
								<Button
									variant="outline"
									pending={updateShare.isPending}
									onClick={() =>
										updateShare.mutate({
											teamId,
											shareId: activeShare.id,
											duration
										})
									}
								>
									Update expiry
								</Button>
								<Button
									variant="destructive-ghost"
									pending={deleteShare.isPending}
									onClick={async () => {
										if (
											await confirmAction({
												title: "Remove share link",
												message: "Remove this public knowledge base link?"
											})
										)
											deleteShare.mutate({ teamId, shareId: activeShare.id })
									}}
									icon={<Trash2Icon />}
								>
									Remove
								</Button>
							</>
						) : (
							<Button
								pending={createShare.isPending}
								onClick={() =>
									createShare.mutate({ teamId, documentId, duration })
								}
							>
								Create link
							</Button>
						)}
					</div>
					{activeShare && (
						<p className="text-xs text-text-small">
							{activeShare.expiresAt
								? `Expires ${activeShare.expiresAt.toLocaleString()}`
								: "Never expires"}
						</p>
					)}
				</Card>
			</DialogContent>
		</Dialog>
	)
}
